import { Popover } from '@mui/material';
import { FormatTime } from 'common/formatters';
import StatusBadge, { getStatusBarColor } from 'common/StatusBadge';
import { getAppointmentDisplayStatus } from '../AppointmentBadge';
import moment from 'moment';

import { useState } from 'react';
import AppointmentBadge from '../AppointmentBadge';

export default function MonthView({ startDate, appointments, handlePopupToggle }) {
    const [popupData, setPopupData] = useState({ open: false, date: null, anchorEl: null });
    const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    let dateAppointmentCount = {};

    const statusColors = {
        booked: { background: 'rgba(117, 189, 47, 0.1)', color: 'rgba(117, 189, 47, 1)' }, // Green for booked
        pending: { background: 'rgba(149, 207, 211, 0.1)', color: 'rgba(149, 207, 211, 1)' }, // Teal for pending
        cancelled: { background: 'rgba(178, 17, 17, 1)', color: 'rgba(180, 35, 24, 1)' }, // Red for cancelled
        cancelled_patient: { background: 'rgba(178, 17, 17, 1)', color: 'rgba(180, 35, 24, 1)' }, // Red for cancelled by patient
        cancelled_provider: { background: 'rgba(178, 17, 17, 1)', color: 'rgba(180, 35, 24, 1)' }, // Red for cancelled by provider
    }

    const createDateString = (date) =>
        `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;

    const getStartOfGrid = (date) => {
        const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        const dayIndex = (firstDayOfMonth.getDay() + 6) % 7;
        return new Date(firstDayOfMonth.setDate(firstDayOfMonth.getDate() - dayIndex));
    };

    const getEndOfGrid = (date) => {
        const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        const dayIndex = (lastDayOfMonth.getDay() + 6) % 7;
        return new Date(lastDayOfMonth.setDate(lastDayOfMonth.getDate() + (6 - dayIndex)));
    };

    const getDatesForGrid = (startDate) => {
        const startOfGrid = getStartOfGrid(startDate);
        const endOfGrid = getEndOfGrid(startDate);
        const dates = [];
        let currentDate = new Date(startOfGrid);

        while (currentDate <= endOfGrid) {
            dates.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }
        return dates;
    };

    const getDateAppointments = (date) => {
        const formattedDate = moment(date).format('DD-MM-YYYY');
        const dateAppointments = appointments?.filter(
            (appointment) => moment(appointment?.appointment_date).format('DD-MM-YYYY') === formattedDate,
        );
        dateAppointmentCount[date] = dateAppointments.length;
        return dateAppointments;
    };

    const dates = getDatesForGrid(startDate);

    // Removed local past checker; we now rely on shared display status logic

    const handleMoreButtonClick = (event, dateString) => {
        const dateSpan = event.currentTarget.closest('.date-container').querySelector('.date-span');
        setPopupData({ open: true, date: dateString, anchorEl: dateSpan });
    };

    const handlePopupClose = () => {
        setPopupData({ open: false, date: null, anchorEl: null });
    };


    return (
        <div>
            <div className="grid grid-cols-7 gap-2">
                {days.map((day) => (
                    <div
                        key={day}
                        className="text-center text-xs uppercase poppins-semibold"
                        style={{ 
                            color: '#4b5563', 
                            backgroundColor: '#E1E1E1',
                            padding: '12px 16px', 
                            borderRadius: '12px 12px 0px 0px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            borderBottom: '#9CA3AF 1px solid'
                        }}
                    >
                        {day}
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-2">
                {dates.map((date, index) => {
                    const dateString = createDateString(date);
                    const isCurrentMonth = date.getMonth() === startDate.getMonth();
                    const isToday = dateString === createDateString(new Date());

                    return (
                        <div
                            key={index + date}
                            className={`bg-white min-h-[120px] p-2 text-black text-sm relative date-container ${isCurrentMonth ? '' : 'opacity-50'}`}
                            style={{
                                borderRadius: '8px',
                                borderTopLeftRadius: index < 7 ? '0px' : '8px',
                                borderTopRightRadius: index < 7 ? '0px' : '8px',
                            }}
                        >
                            <span
                                className="text-lg absolute top-2 right-2 date-span"
                                style={{ color: isToday ? '#ff5f39' : 'black', fontWeight: isToday ? '700' : '500' }}
                            >
                                {date.getDate()}
                            </span>
                            {getDateAppointments(dateString)
                                .slice(0, 2)
                                .map((appointment, idx) => {
                                    return (
                                        <div
                                        key={appointment?.id}
                                        className={`${idx === 0 ? 'mt-6' : 'mt-3'} cursor-pointer hover:bg-structure pl-1 pr-3 py-1 rounded-lg hover:text-primary flex items-center poppins-regular`}
                                        onClick={(e) => handlePopupToggle(e, appointment)}
                                      >
                                        {/* Status Bar */}
                                        <div
                                          className="w-[4px] h-12 min-h-[48px] rounded-full flex-shrink-0"
                                          style={{ backgroundColor: (() => {
                                              const displayStatus = getAppointmentDisplayStatus(
                                                  appointment?.status,
                                                  appointment?.appointment_date,
                                                  appointment?.time
                                              );
                                              return getStatusBarColor(displayStatus);
                                          })() }}
                                        ></div>
                                      
                                        <div className="ml-2 flex-1 flex flex-col justify-center overflow-hidden text-ellipsis">
                                          {/* Provider Name - 12px */}
                                          <div className="text-[12px] font-normal poppins ">
                                            {appointment?.provider_name}
                                          </div>
                                      
                                          {/* Appointment Time + Description  */}
                                          <div className="flex items-center gap-2 text-[10px] text-structure4 overflow-hidden text-ellipsis whitespace-nowrap">
                                            <span className="overflow-hidden text-ellipsis">
                                              {FormatTime(appointment?.time)}, {appointment?.service_data?.[0]?.serviceName || appointment?.service_data?.[0]?.name || appointment?.service_data?.[0]?.description || 'Service'}
                                            </span>
                                          </div>
                                        </div>
                                      </div>


                                    )
                                }
                                )}
                            {dateAppointmentCount[dateString] > 2 && (
                                <div
                                    style={{
                                        borderRadius: '8px',
                                        border: '1px solid #999999',
                                        width: 'fit-content',
                                        padding: '0.4rem 1rem',
                                        marginLeft: '0.5rem',
                                    }}
                                    onClick={(e) => handleMoreButtonClick(e, dateString)}
                                    className="mt-6 cursor-pointer hover:bg-structure px-3 py-1 rounded-lg poppins-semibold fs-12 text-primary items-center text-center"
                                >
                                    <span>... {dateAppointmentCount[dateString] - 2} more</span>
                                </div>
                            )}
                        </div>
                    );
                })}
                <Popover
                    open={popupData.open}
                    anchorEl={popupData.anchorEl}
                    onClose={handlePopupClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    PaperProps={{
                        style: {
                            borderRadius: '12px',
                            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
                            maxWidth: 'none',
                        },
                    }}
                >
                    <div className="p-3 w-55 max-h-96 overflow-y-auto" style={{ backgroundColor: 'white' }}>
                        {getDateAppointments(popupData.date)?.map((appointment, index) => {
                            // Format time to display as "2:30 PM" format
                            const formattedTime = appointment?.time ? 
                                moment(appointment.time, 'HH:mm:ss').format('h:mm A') : 
                                FormatTime(appointment?.time);
                            
                            return (
                                <div
                                    key={appointment.id}
                                    className="cursor-pointer py-3 pl-1 pr-2 flex flex-col hover:text-primary poppins-regular"
                                    onClick={(e) => {
                                        handlePopupClose();
                                        handlePopupToggle(e, appointment);
                                    }}
                                >
                                    <div className="flex items-center w-full">
                                        {/* Status Bar */}
                                        <div
                                            className="w-[4px] h-12 min-h-[48px] rounded-full flex-shrink-0"
                                            style={{ backgroundColor: statusColors[appointment?.status]?.color }}
                                        ></div>
                                        
                                        <div className="ml-2 flex-1 flex flex-col justify-center">
                                            <div className="text-[12px] font-normal poppins">
                                                {appointment?.provider_name || 'Provider Name'}
                                            </div>
                                            
                                            <div className="flex items-center gap-2 text-[10px] text-structure4 overflow-hidden text-ellipsis whitespace-nowrap">
                                                <span className="overflow-hidden text-ellipsis">
                                                    {formattedTime}, {appointment?.service_data?.[0]?.serviceName || appointment?.service_data?.[0]?.name || appointment?.service_data?.[0]?.description || 'Appointment'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Popover>
            </div>
        </div>
    );
}
